import { getValidToken } from '../utils/tokenUtils';
import { getErrorMessage } from '../utils/appErrorMessages';

/**
 * Reads a failed fetch Response and builds an Error with a user-friendly
 * Swedish message plus these extra fields:
 *   err.status     — HTTP status code
 *   err.detail     — raw backend message (original English text, for field-level mapping)
 *   err.errorCode  — MIMIR-XXXX code (if backend returned one)
 *   err.origin     — "CLIENT" | "SERVER" (if backend returned one)
 *
 * Use this for requests that can't go through apiRequest (e.g. FormData uploads
 * that must not send a JSON Content-Type) so error handling stays consistent.
 */
export const buildErrorFromResponse = async (res) => {
  const text = await res.text().catch(() => null);
  let detail    = null;
  let errorCode = null;
  let origin    = null;

  if (text) {
    try {
      const parsed = JSON.parse(text);
      detail    = parsed.message || parsed.error || text;
      errorCode = parsed.errorCode ?? null;
      origin    = parsed.origin ?? null;
    } catch {
      detail = text;
    }
  }

  // 1. Try AppErrorCode-based message first (most specific)
  let message = getErrorMessage(errorCode, origin);

  // 2. Fall back to HTTP-status defaults
  if (!message) {
    switch (res.status) {
      case 401:
        message = 'Din session har gått ut. Logga in igen.';
        break;
      case 403:
        message = 'Du saknar behörighet för denna åtgärd.';
        break;
      case 429:
        message = 'För många förfrågningar. Vänta en stund och försök igen.';
        break;
      case 404:
        message = detail || 'Resursen kunde inte hittas.';
        break;
      case 400:
      case 409:
        message = detail || 'Ogiltig begäran.';
        break;
      default:
        message = `Något gick fel (HTTP ${res.status}). Försök igen.`;
    }
  }

  const err = new Error(message);
  err.status    = res.status;
  err.detail    = detail;
  err.errorCode = errorCode;
  err.origin    = origin;
  return err;
};

/**
 * Wrapper around fetch that adds auth, parses errors and throws Error objects
 * with user-friendly Swedish messages (see buildErrorFromResponse for the
 * extra fields attached to the thrown error).
 */
export const apiRequest = async (url, options = {}) => {
  const token = await getValidToken();
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...options.headers,
  };

  let res;
  try {
    res = await fetch(url, { ...options, headers });
  } catch {
    throw new Error('Kunde inte nå servern. Kontrollera din anslutning och försök igen.');
  }

  if (!res.ok) {
    throw await buildErrorFromResponse(res);
  }

  const ct = res.headers.get('content-type') || '';
  if (res.status === 204 || !ct.includes('application/json')) return null;
  return res.json();
};
